import React, { useState, useEffect } from 'react';
import { 
  Eye, 
  EyeOff, 
  CheckCircle, 
  XCircle, 
  Trash2, 
  Plus,
  Filter,
  Search,
  Calendar,
  User,
  Image as ImageIcon,
  Edit
} from 'lucide-react';
import { useAdsService } from '../../hooks/useAdsService';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import CreateAdModal from '../../components/UI/CreateAdModal';
import EditAdModal from '../../components/UI/EditAdModal';
import { cn } from '../../utils/cn';

const Ads = () => {
  const { getAllAds, deleteAd, loading, error, clearError } = useAdsService();
  const [ads, setAds] = useState([]);
  const [filter, setFilter] = useState('all'); // all, active, inactive
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAd, setSelectedAd] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingAd, setEditingAd] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    loadAds();
  }, []);

  const loadAds = async () => {
    try {
      clearError();
      const allAds = await getAllAds(false); // Don't use cache for admin view
      setAds(allAds);
    } catch (err) {
      console.error('Error loading ads:', err);
      // Error is handled by the hook
    }
  };



  const handleDelete = async (adKey) => {
    if (!window.confirm('Are you sure you want to delete this ad? This action cannot be undone.')) {
      return;
    }

    try {
      setActionLoading(adKey);
      await deleteAd(adKey);
      await loadAds(); // Reload to get updated data
    } catch (err) {
      console.error('Error deleting ad:', err);
      // Error is handled by the hook
    } finally {
      setActionLoading(null);
    }
  };

  const handleEdit = (ad) => {
    setEditingAd(ad);
    setShowEditModal(true);
  };

  const filteredAds = ads.filter(ad => {
    // Apply status filter
    if (filter === 'active' && !ad.isActive) return false;
    if (filter === 'inactive' && ad.isActive) return false;

    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return (
        ad.title?.toLowerCase().includes(searchLower) ||
        ad.description?.toLowerCase().includes(searchLower) ||
        ad.uid?.toLowerCase().includes(searchLower)
      );
    }

    return true;
  });

  const getStatusBadge = (ad) => {
    if (ad.isActive) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircle className="w-3 h-3 mr-1" />
          Active
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          <XCircle className="w-3 h-3 mr-1" />
          Inactive
        </span>
      );
    }
  };

  const openImageModal = (ad) => {
    setSelectedAd(ad);
    setShowImageModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Advertisement Management</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage advertisements created by administrators
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex gap-2">
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Ad
          </button>
          <button
            onClick={loadAds}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <Eye className="w-4 h-4 mr-2" />
            Refresh
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <XCircle className="w-5 h-5 text-red-400 mr-2" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}

      {/* Filters and Search */}
      <div className="bg-white shadow rounded-lg p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          {/* Status Filter */}
          <div className="flex items-center space-x-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">All Ads</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search ads..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white shadow rounded-lg p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Eye className="w-6 h-6 text-gray-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Ads</p>
              <p className="text-2xl font-semibold text-gray-900">{ads.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white shadow rounded-lg p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CheckCircle className="w-6 h-6 text-green-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Active</p>
              <p className="text-2xl font-semibold text-gray-900">
                {ads.filter(ad => ad.isActive).length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white shadow rounded-lg p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <XCircle className="w-6 h-6 text-gray-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Inactive</p>
              <p className="text-2xl font-semibold text-gray-900">
                {ads.filter(ad => !ad.isActive).length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Ads List */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            Advertisements ({filteredAds.length})
          </h3>
          
          {filteredAds.length === 0 ? (
            <div className="text-center py-12">
              <ImageIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No ads found</h3>
              <p className="mt-1 text-sm text-gray-500">
                {searchTerm || filter !== 'all' 
                  ? 'Try adjusting your search or filter criteria.'
                  : 'No advertisements have been submitted yet.'
                }
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredAds.map((ad) => (
                <div
                  key={ad.key}
                  className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-3 mb-2">
                        <h4 className="text-sm font-medium text-gray-900 truncate">
                          {ad.title || 'Untitled Ad'}
                        </h4>
                        {getStatusBadge(ad)}
                      </div>
                      
                      {/* Ad Thumbnail */}
                      {ad.imageUrl && (
                        <div className="mb-3">
                          <div className="relative w-32 h-20 bg-gray-100 rounded-lg overflow-hidden border border-gray-200">
                            <img
                              src={ad.imageUrl}
                              alt={ad.title || 'Ad thumbnail'}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                            <div className="hidden absolute inset-0 items-center justify-center bg-gray-100">
                              <ImageIcon className="w-6 h-6 text-gray-400" />
                            </div>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">Horizontal Ad Preview</p>
                        </div>
                      )}
                      
                      {ad.description && (
                        <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                          {ad.description}
                        </p>
                      )}

                                             <div className="flex items-center space-x-4 text-xs text-gray-500">
                         <div className="flex items-center">
                           <User className="w-3 h-3 mr-1" />
                           {ad.uid}
                         </div>
                         <div className="flex items-center">
                           <Calendar className="w-3 h-3 mr-1" />
                           {ad.createdDate}
                         </div>
                         {ad.modifiedDate && (
                           <div className="flex items-center">
                             <span className="mr-1">Modified:</span>
                             {ad.modifiedDate}
                           </div>
                         )}
                         <div className="flex items-center">
                           <span className="mr-1">Hits:</span>
                           {ad.hits || 0}
                         </div>

                         {ad.isActive && (
                           <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                             Active
                           </span>
                         )}
                       </div>
                    </div>

                    <div className="flex items-center space-x-2 ml-4">
                      {/* View Images Button */}
                      <button
                        onClick={() => openImageModal(ad)}
                        className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      >
                        <ImageIcon className="w-3 h-3 mr-1" />
                        View
                      </button>

                      {/* Edit Button */}
                      <button
                        onClick={() => handleEdit(ad)}
                        className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        <Edit className="w-3 h-3 mr-1" />
                        Edit
                      </button>

                      {/* Delete Button */}
                      <button
                        onClick={() => handleDelete(ad.key)}
                        disabled={actionLoading === ad.key}
                        className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                      >
                        {actionLoading === ad.key ? (
                          <LoadingSpinner size="sm" />
                        ) : (
                          <>
                            <Trash2 className="w-3 h-3 mr-1" />
                            Delete
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Image Modal */}
      {showImageModal && selectedAd && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    {selectedAd.title || 'Untitled Ad'}
                  </h3>
                  <button
                    onClick={() => setShowImageModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircle className="w-6 h-6" />
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Landscape Image</h4>
                    {selectedAd.imageUrl ? (
                      <img
                        src={selectedAd.imageUrl}
                        alt="Landscape"
                        className="w-full h-48 object-cover rounded-lg border border-gray-200"
                      />
                    ) : (
                      <div className="w-full h-48 bg-gray-100 rounded-lg flex items-center justify-center">
                        <span className="text-gray-500">No landscape image</span>
                      </div>
                    )}
                  </div>
                  <div className="flex justify-center">
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Portrait Image</h4>
                      {selectedAd.imagePortUrl ? (
                        <div style={{ width: '200px', aspectRatio: '9/16' }}>
                          <img
                            src={selectedAd.imagePortUrl}
                            alt="Portrait"
                            className="w-full h-full object-cover rounded-lg border border-gray-200"
                          />
                        </div>
                      ) : (
                        <div 
                          className="bg-gray-100 rounded-lg flex items-center justify-center"
                          style={{ width: '200px', aspectRatio: '9/16' }}
                        >
                          <span className="text-gray-500">No portrait image</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {selectedAd.description && (
                  <div className="mt-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Description</h4>
                    <p className="text-sm text-gray-600">{selectedAd.description}</p>
                  </div>
                )}

                                 <div className="mt-4 grid grid-cols-2 gap-4 text-sm text-gray-500">
                   <div>
                     <span className="font-medium">User ID:</span> {selectedAd.uid}
                   </div>
                   <div>
                     <span className="font-medium">Created:</span> {selectedAd.createdDate}
                   </div>
                   {selectedAd.modifiedDate && (
                     <div>
                       <span className="font-medium">Modified:</span> {selectedAd.modifiedDate}
                     </div>
                   )}
                   <div>
                     <span className="font-medium">Status:</span> {selectedAd.statusText}
                   </div>
                   <div>
                     <span className="font-medium">Hits:</span> {selectedAd.hits || 0}
                   </div>
                   <div>
                     <span className="font-medium">Last Hit:</span> {selectedAd.lastHitDate}
                   </div>

                   <div>
                     <span className="font-medium">Active:</span> {selectedAd.isActive ? 'Yes' : 'No'}
                   </div>


                 </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Ad Modal */}
      <CreateAdModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreated={(newAd) => {
          setAds(prevAds => [newAd, ...prevAds]);
          setShowCreateModal(false);
        }}
      />

      {/* Edit Ad Modal */}
      <EditAdModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingAd(null);
        }}
        onUpdated={(updatedAd) => {
          setAds(prevAds => prevAds.map(ad => ad.key === updatedAd.key ? updatedAd : ad));
          setShowEditModal(false);
          setEditingAd(null);
        }}
        ad={editingAd}
      />
    </div>
  );
};

export default Ads; 